import React, { useState, useEffect } from "react";
import { getImage } from "gatsby-plugin-image";
import { graphql } from "gatsby";
import Seo from "../components/seo";
import FloorplanSchemaMarkup from "../components/FloorplanSchemaMarkup";
import { safeFetch, safeParseUnitsData, safeParseDealsData, createSectionErrorLogger } from "../utils/apiUtils";
import CommunityHeader from "../components/communityHeader";
import SlugWrapCommunityFooter from "../components/slugWrapCommunityFooter";
import ContactFormRHF from "../components/contactFormRHF";
import {
  hoverTitle,
  hoverTitleWrap,
  fontSizeFive,
  fontColorBlue,
  fontColorDark,
} from "../components/shared.module.scss";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { v4 as uuidv4 } from "uuid";
import UnitDisplayCards from "../components/unitDisplayCards";
import { getYardiPropId } from "../utils/propertyMapUtil";
import { extractCityAndState } from "../utils/helperFunctions";
import { useSessionContext } from "../context/SessionContext";
import useArrivalInfo from "../utils/useArrivalInfo";
import { transformDeals, transformUnits, getFloorplanNameYardi } from "../utils/floorplanTransforms";
import { selectUnitsForDisplay } from "../utils/floorplanUnitDisplayStrategies";
import { metaPixel } from "../utils/metaPixelTracking";

const FloorplanPage = ({ data }) => {
  const [avCount, setAvCount] = useState(0);
  const [cid, setCid] = useState(data.contentfulCommunity.rmpropid);
  const [units, setUnits] = useState([]);
  const [floorplanResults, setFloorplanResults] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [deals, setDeals] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDollhouse, setShowDollhouse] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Add arrival info and session context for UTM tracking
  const { sessionId } = useSessionContext();
  const arrivalInfo = useArrivalInfo();

  useEffect(() => {
    //Check if we are on a mobile size screen
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
      window.addEventListener("resize", handleResize);
      handleResize();
      
      // Track Meta Pixel ViewContent event for floorplan page view
      metaPixel.trackViewContent(
        `${data.contentfulFloorplans.slug}_floorplan`, 
        'floorplan', 
        {
          content_name: data.contentfulFloorplans.floorplanName,
          content_category: 'Floorplan Page',
          property_name: data.contentfulCommunity.communityName,
          property_slug: data.contentfulCommunity.slug,
          beds: data.contentfulFloorplans.beds,
          baths: data.contentfulFloorplans.baths,
          sqft: data.contentfulFloorplans.sqFt,
        }
      );
      
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);


  useEffect(() => {
    const yardi_cid = getYardiPropId(data.contentfulCommunity.rmpropid);
    
    // Create error logger for this section
    const logError = createSectionErrorLogger('floorplan_template', {
      yardi_cid: yardi_cid,
      community: data.contentfulCommunity.communityName,
      community_slug: data.contentfulCommunity.slug,
      rmpropid: data.contentfulCommunity.rmpropid
    });
    
    // Context for all API calls
    const apiContext = {
      section: 'floorplan_template',
      yardi_cid: yardi_cid,
      community: data.contentfulCommunity.communityName,
      community_slug: data.contentfulCommunity.slug,
      rmpropid: data.contentfulCommunity.rmpropid
    };

    // Load data with individual error handling
    const loadData = async () => {
      try {
        // Initialize with default values
        let unitsResult = [];
        let dealsResult = {};

        // 1) Fetch main units - critical
        const mainUnitsData = await safeFetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=${yardi_cid}`,
          "main units",
          apiContext
        );
        if (mainUnitsData) {
          try {
            const parsedUnits = parseUnitsData(mainUnitsData);
            unitsResult = unitsResult.concat(parsedUnits);
          } catch (error) {
            console.error("Error parsing main units data:", error);
            logError(error, 'parse_main_units');
          }
        }

        // Additional building fetches for multi-building communities - critical for complete data
        const additionalFetches = [];

        if (yardi_cid == "beachfl1") {
          additionalFetches.push(
            safeFetch(
              `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl2`,
              "beachfl2 units",
              apiContext
            ),
            safeFetch(
              `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl3`,
              "beachfl3 units",
              apiContext
            )
          );
        }
        if (yardi_cid === "sc1") {
          additionalFetches.push(
            safeFetch(
              `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc2`,
              "sc2 units",
              apiContext
            ),
            safeFetch(
              `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc3`,
              "sc3 units",
              apiContext
            )
          );
        }
        if (yardi_cid === "golfla12") {
          additionalFetches.push(
            safeFetch(
              `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=golfla03`,
              "golfla03 units",
              apiContext
            )
          );
        }
        if (yardi_cid === "willfla1") {
          additionalFetches.push(
            safeFetch(
              `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=willfla2`,
              "willfla2 units",
              apiContext
            )
          );
        }
        if (yardi_cid === "mcdonald") {
          additionalFetches.push(
            safeFetch(
              `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=gleno2`,
              "gleno2 units",
              apiContext
            )
          );
        }

        // Fetch deals - critical for pricing/promotions
        const dealsPromise = safeFetch(
          "https://redisut.mcklab.com/index.php?action=getDeals",
          "deals",
          apiContext
        );

        // Wait for all additional fetches
        const [dealsData, ...additionalUnitsData] = await Promise.all([
          dealsPromise,
          ...additionalFetches
        ]);

        // Process deals if available
        if (dealsData) {
          dealsResult = safeParseDealsData(dealsData, 'deals', apiContext);
        }

        // Process additional units data
        additionalUnitsData.forEach((data, index) => {
          if (data) {
            try {
              const parsedData = parseUnitsData(data);
              unitsResult = unitsResult.concat(parsedData);
            } catch (error) {
              console.error(`Error parsing additional units data ${index}:`, error);
              logError(error, `parse_additional_units_${index}`);
            }
          }
        });

        // Apply community-specific transformations using the unified utility
        unitsResult = transformUnits(unitsResult, yardi_cid, { updateFloorplanName: true, cid });

        const noDollhouseCommunities = [401810, 401910, 401830, 401520];
        if (noDollhouseCommunities.includes(cid)) {
          setShowDollhouse(false);
        }

        // Clean up deals - only if we have deals data
        if (Array.isArray(dealsResult) && dealsResult.length > 0) {
          dealsResult = transformDeals(dealsResult, cid);
          // Additional template-specific transformations
          dealsResult.forEach((r) => {
            if (r.sdesc == "2bd x 1.5ba - 1000sf - Chatham") {
              r.sdesc1 = r.scode1;
            }
            if (r.sdesc1 == "$1") {
              r.sdesc1 = "$1,000 Off First Month's Rent";
            }
          });
        }

        // Always set the data (even if empty arrays) and remove loading state
        setDeals(dealsResult);
        setUnits(unitsResult);
        setLoading(false);

      } catch (error) {
        logError(error, 'load_data_main');
        
        // Set empty defaults and stop loading
        setDeals({});
        setUnits([]);
        setLoading(false);
      }
    };

    // Start loading data
    loadData();
  }, [data.contentfulCommunity.rmpropid]);

  const parseUnitsData = (data) => {
    const resData = JSON.parse(data.res);
    const avnow = ["Vacant Unrented Not Ready", "Vacant Unrented Ready"];
    return resData.map((unit) => ({
      unit: unit.unit,
      isVacant: avnow.includes(unit.status),
      status: unit.status,
      marketRent: unit.rent1,
      proformaRent: unit.rent2,
      extra: unit.position,
      amenities: unit.amenities,
      moveOutDate: unit.date1,
      otherDate: unit.date2,
      floorplanName: getFloorplanNameYardi(unit.amenities),
    }));
  };

  useEffect(() => {
    const findFloorplanUnits = (unitsList, floorplanName, displayOption) => {
      // Deal with trailing plus sign on floorplan names
      if (floorplanName.endsWith("+")) {
        floorplanName =
          data.contentfulCommunity.rmpropid == "401460" ||
          data.contentfulCommunity.rmpropid == "401300" ||
          data.contentfulCommunity.rmpropid == "406470" ||
          data.contentfulCommunity.rmpropid == "400550" ||
          data.contentfulCommunity.rmpropid == "400220" ||
          data.contentfulCommunity.rmpropid == "401730"
            ? floorplanName
            : floorplanName.slice(0, -1);
      }

      const filteredUnits = unitsList.filter(
        (unit) => unit.floorplanName.trim() === floorplanName.trim()
      );
      
      const vacantUnits = filteredUnits.filter((unit) => unit.isVacant);
      const avsoon = ["Notice Unrented"];
      const noticeUnits = filteredUnits.filter((unit) =>
        avsoon.includes(unit.status)
      );

      let combinedUnits = [
        ...vacantUnits.sort(
          (a, b) => Number(a.marketRent) - Number(b.marketRent)
        ),
        ...noticeUnits.sort(
          (a, b) => new Date(a.moveOutDate) - new Date(b.moveOutDate)
        ),
      ];

      if (combinedUnits.length === 0) {
        return { hasVacantUnits: false };
      }

      setAvCount(combinedUnits.length);

      // Apply unit order override if specified
      if (
        data.contentfulFloorplans.unitOrderOverride &&
        data.contentfulFloorplans.unitOrderOverride.length > 0
      ) {
        const Ounits = data.contentfulFloorplans.unitOrderOverride;
        // Move them in reverse order so the first item is moved last
        for (let i = 2; i >= 0; i--) {
          if (
            Ounits[i] &&
            Ounits[i] !== "na" &&
            Ounits[i] !== "none" &&
            Ounits[i] !== "default"
          ) {
            const index = combinedUnits.findIndex((unit) => unit.ID === Ounits[i]);
            if (index !== -1) {
              const [unitToMove] = combinedUnits.splice(index, 1);
              combinedUnits.unshift(unitToMove);
            }
          }
        }
      }

      // Use the new selection strategy
      if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
        console.log('Display Option from Contentful:', displayOption);
        console.log('Available units count:', combinedUnits.length);
      }
      
      const selectionResult = selectUnitsForDisplay(
        displayOption || 'Lowest priced unit',
        combinedUnits,
        deals
      );
      
      if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
        console.log('Selection result:', selectionResult);
      }

      if (!selectionResult.hasUnits) {
        return { hasVacantUnits: false };
      }

      // Map to the expected format for backward compatibility
      const selectedUnits = selectionResult.units;
      return {
        hasVacantUnits: true,
        units: selectedUnits,
        displayStrategy: selectionResult.strategy,
        // Keep old properties for backward compatibility
        lowestPricedUnit: selectedUnits[0] || null,
        middlePricedUnit: selectedUnits[1] || null,
        highestPricedUnit: selectedUnits[2] || null,
      };
    };

    const floorplanResultsData = findFloorplanUnits(
      units,
      data.contentfulFloorplans.floorplanName,
      data.contentfulFloorplans.unitDisplayOptions
    );
    setFloorplanResults(floorplanResultsData);
  }, [units, data.contentfulFloorplans, deals]);

  let images = [];
  if (data.contentfulFloorplans.floorplanImages) {
    let icount = 0;
    data.contentfulFloorplans.floorplanImages.forEach((i) => {
      images.push({ img: getImage(i), title: i.title, icount });
      icount += 1;
    });
  }

  const mapInSlices = (array, sliceSize, sliceFunc) => {
    const out = [];
    for (let i = 0; i < array.length; i += sliceSize) {
      const slice = array.slice(i, i + sliceSize);
      out.push(sliceFunc(slice, i));
    }
    return out;
  };

  const matterportUrl =
    data.contentfulFloorplans.matterportTour &&
    data.contentfulFloorplans.matterportTour.length > 0 &&
    data.contentfulFloorplans.matterportTour !== "none"
      ? data.contentfulFloorplans.matterportTour
      : false;

  const moreMatterportUrls =
    data.contentfulFloorplans.additionalMatterports &&
    data.contentfulFloorplans.additionalMatterports.length > 0
      ? data.contentfulFloorplans.additionalMatterports
      : false;

  // -----------------------------
  // NEW: Parse floorplanVideo (YouTube watch -> embed)
  // Support both youtube.com/watch?v= and youtu.be/ formats
  // -----------------------------
  let floorplanVideo =
    data.contentfulFloorplans.galleryVideo &&
    data.contentfulFloorplans.galleryVideo.length > 0 &&
    data.contentfulFloorplans.galleryVideo.toLowerCase() !== "none"
      ? data.contentfulFloorplans.galleryVideo
      : false;

  if (floorplanVideo) {
    let videoId = null;
    
    // Handle youtube.com/watch?v= format
    if (floorplanVideo.includes("watch?v=")) {
      videoId = floorplanVideo.split("watch?v=")[1];
      // Remove any additional parameters after the video ID
      if (videoId.includes("&")) {
        videoId = videoId.split("&")[0];
      }
    }
    // Handle youtu.be/ format
    else if (floorplanVideo.includes("youtu.be/")) {
      videoId = floorplanVideo.split("youtu.be/")[1];
      // Remove any additional parameters after the video ID
      if (videoId.includes("?")) {
        videoId = videoId.split("?")[0];
      }
    }
    
    // Convert to embed URL if we extracted a video ID
    if (videoId) {
      floorplanVideo = `https://www.youtube.com/embed/${videoId}`;
    }
  }
  // -----------------------------

  const formatTitle = (title) => {
    let formattedTitle = title
      .replace(/Bed/g, "Bedroom")
      .replace(/Bath/g, "Bathroom");

    formattedTitle = formattedTitle.replace(/\+/g, (match) => {
      return `<span class="plus-symbol">${match}</span>`;
    });

    if (isMobile) {
      const parts = formattedTitle.split(" - ");
      if (parts.length === 3) {
        formattedTitle = `<span class="mobile-floorplan-title">${parts[0]}</span><br />${parts[1]}<br />${parts[2]}`;
      } else if (parts.length === 4) {
        formattedTitle = `<span class="mobile-floorplan-title">${parts[0]}</span><br />${parts[1]}<br />${parts[2]}<br />${parts[3]}`;
      }
    }
    return formattedTitle;
  };

  function formatCityName(cityName) {
    return cityName
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  const hoursData = data.allContentfulHours.edges ?? [];
  const communityHoursDataArray = hoursData
    .filter(
      ({ node }) =>
        node.community &&
        node.community.some(
          (community) => community.slug === data.contentfulCommunity.slug
        ) &&
        node.active
    )
    .sort((a, b) => b.node.priority - a.node.priority);

  const communityHoursData =
    communityHoursDataArray.length > 0 ? communityHoursDataArray[0] : null;
//console.log(deals);
  return (
    <main>
      <CommunityHeader
        cname={data.contentfulCommunity.communityName}
        phone={data.contentfulCommunity.phone}
        address={data.contentfulCommunity.address}
        slug={data.contentfulCommunity.slug}
        area={data.contentfulCommunity.area}
        citySlug={data.contentfulCommunity.citySlug}
        actOveride={true}
        hours={data.contentfulCommunity.hours.hours}
        hoursData={communityHoursData ? communityHoursData.node.hours : null}
      />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <div className=" container" id="indiv_flrpln_page">
        <div className="columns is-mobile">
          <div className="column ">
            {!isMobile && (
              <h1
                className={`${fontSizeFive} ${fontColorBlue}`}
                style={{
                  textAlign: "center",
                  fontFamily: "MoonTime",
                  position: "relative",
                }}
                dangerouslySetInnerHTML={{
                  __html: formatTitle(data.contentfulFloorplans.floorplanTitle),
                }}
              ></h1>
            )}
            {isMobile && (
              <h1
                className={fontColorBlue}
                style={{ textAlign: "center" }}
                dangerouslySetInnerHTML={{
                  __html: formatTitle(data.contentfulFloorplans.floorplanTitle),
                }}
              ></h1>
            )}

            <p style={{ textAlign: "center" }} className={fontColorBlue}>
              No Extra Hidden Monthly Fees!
            </p>

            <br />
            {!isMobile &&
              data.contentfulFloorplans.floorplanDescription &&
              data.contentfulFloorplans.floorplanDescription && (
                <p
                  style={{ padding: "0 1rem 2rem", textAlign: "center" }}
                  className={fontColorDark}
                >
                  {
                    data.contentfulFloorplans.floorplanDescription
                      .floorplanDescription
                  }
                </p>
              )}
            {!loading && (
              <>
                {floorplanResults && floorplanResults.hasVacantUnits ? (
                  <UnitDisplayCards
                    floorplanResults={floorplanResults}
                    slug={data.contentfulFloorplans.communitySlug}
                    floorplanName={data.contentfulFloorplans.floorplanName}
                    floorplanBeds={data.contentfulFloorplans.beds}
                    utilitiesIncluded={
                      data.contentfulCommunity.utilitiesIncluded ||
                      data.contentfulFloorplans.utilitiesIncluded
                    }
                    deals={deals}
                    showDollhouse={showDollhouse}
                    dollhouse={
                      data.contentfulFloorplans.bannerImage
                        ? data.contentfulFloorplans.bannerImage.gatsbyImageData
                        : ""
                    }
                    calloutFieldText={data.contentfulFloorplans.calloutField}
                    communityName={data.contentfulCommunity.communityName}
                    email={data.contentfulCommunity.email}
                  />
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    margin: '1rem 0' 
                  }}>
                    <h3 style={{ marginBottom: '1rem' }}>
                      No Available Units Currently
                    </h3>
                    <p style={{ marginBottom: '1rem' }}>
                      While we don't have any units available for this floorplan right now, 
                      please contact us to learn about upcoming availability and to be added to our waitlist.
                    </p>
                    <p style={{ 
                      fontSize: '0.9rem', 
                      color: '#666',
                      fontStyle: 'italic'
                    }}>
                      Note: Some unit information may be temporarily unavailable due to system maintenance.
                    </p>
                  </div>
                )}
              </>
            )}
            <br />
            <p style={{ textAlign: "center" }}>
              {data.contentfulCommunity.communityName} Apartments in{" "}
              {formatCityName(data.contentfulCommunity.citySlug)},{" "}
              {data.contentfulCommunity.area}
            </p>
            {isMobile &&
              data.contentfulFloorplans.floorplanDescription &&
              data.contentfulFloorplans.floorplanDescription && (
                <p
                  style={{ padding: "0 1rem 2rem", textAlign: "center" }}
                  className={fontColorDark}
                >
                  {
                    data.contentfulFloorplans.floorplanDescription
                      .floorplanDescription
                  }
                </p>
              )}
            <br />
            <hr />
            {matterportUrl && matterportUrl !== "None" && (
              <div
                style={{ width: "100%", height: "500px", position: "relative" }}
              >
                <iframe
                  src={matterportUrl}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            )}
            {moreMatterportUrls &&
              moreMatterportUrls.map((murl) => (
                <div
                  key={murl}
                  style={{
                    width: "100%",
                    height: "500px",
                    position: "relative",
                    marginTop: "1rem",
                  }}
                >
                  <iframe
                    src={murl}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              ))}
            <br />
            {mapInSlices(images, 3, (imageSlice, sliceIndex) => (
              <div
                className="columns"
                key={uuidv4()}
                style={{ marginBottom: "10px" }}
              >
                {imageSlice.map((img) => {
                  return (
                    <div
                      className={`${hoverTitleWrap} column is-one-third`}
                      key={img.icount}
                      style={{
                        maxHeight: "288px",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        marginBottom: "20px",
                      }}
                    >
                      <img
                        src={img.img.images.fallback.src}
                        alt={img.title}
                        onClick={() => {
                          setLightboxOpen(true);
                          setCurrentImage(img.icount);
                        }}
                      />
                      <p className={hoverTitle} id="floorplan_image_hover_title">
                        {img.title}
                      </p>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* 
              NEW: Render the YouTube video if present 
            */}
            {floorplanVideo && (
              <div
                style={{
                  width: "100%",
                  height: "500px",
                  position: "relative",
                  marginBottom: "2rem",
                  marginTop: "2rem",
                }}
              >
                <iframe
                  src={floorplanVideo}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  allowFullScreen
                  loading="lazy"
                  title="Floorplan Video"
                ></iframe>
              </div>
            )}
            
            {/* Contact Form - Always visible, positioned after all media */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '3rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                width: '100%',
                maxWidth: '50vw',
                minWidth: '300px'
              }}>
                <h3 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  {(!floorplanResults || !floorplanResults.hasVacantUnits) ? 'No available units, please contact us:' : 'Contact Us About This Floorplan'}
                </h3>
                <ContactFormRHF
                  toEmail={data.contentfulCommunity.email}
                  appendToBody={`Floorplan: ${data.contentfulFloorplans.floorplanTitle}`}
                  uid={`floorplan-contact-form ${(!floorplanResults || !floorplanResults.hasVacantUnits) ? 'no-available' : ''} ${data.contentfulFloorplans.floorplanSlug}`}
                  slug={data.contentfulCommunity.slug}
                  arrivalInfo={arrivalInfo}
                  sessionId={sessionId}
                  communityName={data.contentfulCommunity.communityName}
                />
              </div>
            </div>
          </div>
        </div>
        <a
          href={`/apartments/${data.contentfulCommunity.area.toLowerCase()}/${data.contentfulCommunity.citySlug.toLowerCase()}/${data.contentfulCommunity.slug.toLowerCase()}/floorplans/`}
        >
          &lt; back to floorplans
        </a>
      </div>
      <br />
      <br />
      <SlugWrapCommunityFooter slug={data.contentfulFloorplans.communitySlug} />
      {lightboxOpen && (
        <Lightbox
          mainSrc={images[currentImage].img.images.fallback.src}
          nextSrc={
            images[(currentImage + 1) % images.length].img.images.fallback.src
          }
          prevSrc={
            images[(currentImage + images.length - 1) % images.length].img.images
              .fallback.src
          }
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() =>
            setCurrentImage(
              (currentImage + images.length - 1) % images.length
            )
          }
          onMoveNextRequest={() =>
            setCurrentImage((currentImage + 1) % images.length)
          }
        />
      )}
    </main>
  );
};

export const data = graphql`
  query ($communitySlug: String, $floorplanSlug: String) {
    contentfulFloorplans(floorplanSlug: { eq: $floorplanSlug }) {
      id
      communitySlug
      floorplanImages {
        gatsbyImageData
        title
      }
      baths
      numBaths
      beds
      sqFt
      floorplanName
      floorplanTitle
      floorplanSlug
      matterportTour
      additionalMatterports
      utilitiesIncluded
      bannerImage {
        gatsbyImageData
      }
      floorplanDescription {
        floorplanDescription
      }
      seoDescription {
        seoDescription
      }
      unitOrderOverride
      galleryVideo
      calloutField
      unitDisplayOptions
    }
    contentfulCommunity(slug: { eq: $communitySlug }) {
      id
      address
      area
      citySlug
      phone
      email
      communityName
      description
      seoDescription {
        seoDescription
      }
      slug
      hours {
        hours
      }
      shortHours
      exactLocation {
        lat
        lon
      }
      rmpropid
      utilitiesIncluded
    }
    allContentfulHours {
      edges {
        node {
          hoursType
          active
          priority
          hours
          community {
            communityName
            slug
          }
        }
      }
    }
  }
`;

export const Head = ({ data }) => {
  // Find active hours data for this community
  const hoursData = data.allContentfulHours?.edges?.find(
    ({ node }) =>
      node.community &&
      node.community.some(
        (community) => community.slug === data.contentfulCommunity.slug
      ) &&
      node.active
  )?.node?.hours;

  // Prepare community data with hoursData
  const communityData = {
    ...data.contentfulCommunity,
    hoursData
  };

  return (
    <>
      <Seo
        title={`${data.contentfulFloorplans.floorplanName}
for ${data.contentfulCommunity.communityName} apartments in
${extractCityAndState(data.contentfulCommunity.address)} |
${data.contentfulFloorplans.beds} bedrooms ${
          data.contentfulFloorplans.baths
            ? data.contentfulFloorplans.baths
            : data.contentfulFloorplans.numBaths
        } bath Floorplans`}
        description={
          data.contentfulCommunity.seoDescription
            ? data.contentfulCommunity.seoDescription.seoDescription
            : data.contentfulCommunity.description
        }
      />
      <FloorplanSchemaMarkup
        communityData={communityData}
        floorplanData={data.contentfulFloorplans}
      />
    </>
  );
};

export default FloorplanPage;
